<template>
  <div>
    <AisDynamicWidgets
      :facets="['*']"
      class="flex flex-col"
    >
      <!-- Custom Category Filter -->
      <AisPanel
        v-show="!isHiddenFacet('productCustomCategories')"
        :class-names="aisPanelClasses"
      >
        <template #header="{ hasRefinements }">
          <refinement-heading
            v-if="hasRefinements"
            name="Categories"
            attribute="productCustomCategories"
          />
        </template>
        <AisRefinementList
          :transform-items="transformFacetItems"
          :class-names="refinementListClasses"
          aria-labelledby="filter-productCustomCategories-label"
          attribute="productCustomCategories"
          operator="or"
        />
      </AisPanel>

      <!-- Blog Category Filter -->
      <AisPanel
        v-show="!isHiddenFacet('blogCategories')"
        :class-names="aisPanelClasses"
      >
        <template #header="{ hasRefinements }">
          <refinement-heading
            v-if="hasRefinements"
            name="Blog Categories"
            attribute="blogCategories"
          />
        </template>
        <AisRefinementList
          :transform-items="transformFacetItems"
          show-more
          :limit="20"
          :show-more-limit="1000"
          :class-names="refinementListClasses"
          aria-labelledby="filter-blogCategories-label"
          attribute="blogCategories"
        />
      </AisPanel>

      <!-- Hit Type Filter -->
      <AisPanel
        v-show="searchType !== HitType.BLOG && !isHiddenFacet('hitType')"
        :class-names="aisPanelClasses"
      >
        <template #header="{ hasRefinements }">
          <refinement-heading
            v-if="hasRefinements"
            name="Type"
            attribute="hitType"
          />
        </template>
        <AisRefinementList
          :transform-items="transformFacetItems"
          :class-names="refinementListClasses"
          aria-labelledby="filter-hitType-label"
          attribute="hitType"
          operator="or"
        />
      </AisPanel>

      <!-- Level Filter -->
      <AisPanel
        v-show="!isHiddenFacet('level')"
        :class-names="aisPanelClasses"
      >
        <template #header="{ hasRefinements }">
          <refinement-heading
            v-if="hasRefinements"
            name="Levels"
            attribute="level"
          />
        </template>
        <AisRefinementList
          :transform-items="transformFacetItems"
          :class-names="refinementListClasses"
          aria-labelledby="filter-level-label"
          attribute="level"
          operator="or"
        />
      </AisPanel>

      <!-- Language Filter -->
      <AisPanel
        v-show="!isHiddenFacet('language') && searchType !== SearchType.SITE"
        :class-names="aisPanelClasses"
      >
        <template #header="{ hasRefinements }">
          <refinement-heading
            v-if="hasRefinements"
            name="Languages"
            attribute="language"
          />
        </template>
        <AisRefinementList
          :transform-items="transformFacetItems"
          :limit="5"
          :show-more-limit="20"
          :show-more="true"
          :class-names="refinementListClasses"
          aria-labelledby="filter-language-label"
          attribute="language"
          operator="or"
        />
      </AisPanel>

      <!-- Themes Filter -->
      <AisPanel
        v-show="!isHiddenFacet('themes')"
        :class-names="aisPanelClasses"
      >
        <template #header="{ hasRefinements }">
          <refinement-heading
            v-if="hasRefinements"
            name="Themes"
            attribute="themes"
          />
        </template>
        <AisRefinementList
          :transform-items="transformFacetItems"
          :limit="5"
          :show-more-limit="20"
          :show-more="true"
          :class-names="refinementListClasses"
          aria-labelledby="filter-theme-label"
          attribute="themes"
          operator="or"
        />
      </AisPanel>
    </AisDynamicWidgets>

    <!-- Price filter-->
    <AisPanel
      v-show="searchType === SearchType.PRODUCT"
      :class-names="aisPanelClasses"
    >
      <template #header="{ hasRefinements }">
        <refinement-heading
          v-if="hasRefinements"
          name="Pricing"
          attribute="price"
        />
      </template>
      <AisNumericMenu
        aria-labelledby="filter-price-label"
        attribute="price"
        :class-names="{
          'ais-NumericMenu-radio': 'text-blue-600 border border-gray-300',
          'ais-NumericMenu-label': 'flex items-center gap-2 text-sm font-medium text-gray-blue-800',
          'ais-NumericMenu-list': 'flex flex-col gap-2.5',
        }"
        :items="[
          { label: 'All' },
          { label: 'less than $5', end: 5 },
          { label: '$5-10', start: 5, end: 10 },
          { label: '$10-25', start: 10, end: 25 },
          { label: '$25-50', start: 25, end: 50 },
          { label: '$50-100', start: 50, end: 100 },
          { label: '$100-200', start: 100, end: 200 },
          { label: '$200+', start: 200 },
        ]"
      />
    </AisPanel>
  </div>
  <AisClearRefinements
    :class-names="{
      'ais-ClearRefinements-button': 'btn btn--sm btn--primary'
    }"
  />
</template>

<script lang="ts">
export default { name: 'FilterSidebar' }
</script>

<script setup lang="ts">
import { storeToRefs } from 'pinia'

import {
  AisNumericMenu,
  AisRefinementList,
  AisClearRefinements,
  AisDynamicWidgets,
  AisPanel,
} from 'vue-instantsearch/vue3/es'

import RefinementHeading from './RefinementHeading.vue'
import { useSearchStore } from '~/store/modules/search'

import { SearchType } from '~/components/search/enums/SearchType'
import { HitType } from '~/components/search/enums/HitType'

const searchStore = useSearchStore()
const {
  hiddenFacets,
  searchType,
} = storeToRefs(searchStore)

const transformFacetItems = items => items.map(item => ({
  ...item,
  label: searchStore.dictionaryLookup(item.value),
  count: `(${item.count})`,
}))

const isHiddenFacet = (facetName: string) =>
  (hiddenFacets.value || []).includes(facetName) ||
  ([SearchType.SITE].includes(searchType.value) && facetName !== 'hitType')

const refinementListClasses = {
  'ais-RefinementList-list': 'flex flex-col gap-2.5',
  'ais-RefinementList-count': 'text-gray-400',
  'ais-RefinementList-checkbox': 'text-blue-600 border border-gray-300',
  'ais-RefinementList-label': 'inline-flex items-top gap-2 text-sm font-medium text-gray-blue-800 cursor-pointer hover:text-gray-blue-900',
  'ais-RefinementList-labelText': '[overflow-wrap:anywhere] capitalize',
  'ais-RefinementList-showMore': 'font-medium mt-2.5 text-sm inline-flex gap-2 items-center focus:outline-none focus-visible:outline-none focus-visible:ring focus-visible:ring-offset-2 text-blue-600 hover:text-blue-700 group-hover:text-blue-700 focus-visible:ring-blue-600',
  'ais-RefinementList-showMore--disabled': '!hidden',
}

const aisPanelClasses = {
  'ais-Panel': 'mb-5',
  'ais-Panel--noRefinement': '!mb-0',
}
</script>
